import { Email, Event, Telegram } from "@mui/icons-material";
import { Box, Card, Link, Typography } from "@mui/joy";
import { useEffect } from "react";
import { useSearchParams } from "react-router";

import { LandingLayout } from "../LandingLayout/LandingLayout";
import { LandingSection } from "../LandingLayout/LandingSection";
import { TeamMemberCard } from "./components/TeamMemberCard/TeamMemberCard";
import { FadeIn } from "~/common/components/FadeIn/FadeIn";

export const ABOUT_US_SECTIONS = {
  CONTACT: "contact",
} as const;

export interface TeamMember {
  name: string;
  moreAboutName: string;
  position: string;
  description: string;
  email: string;
  photoUrl: string;
  linkedIn?: string;
  telegram?: string;
}

export const TEAM_MEMBERS: TeamMember[] = [
  {
    name: "Danil Makarov",
    moreAboutName: "Даніла",
    position: "Co-founder & CTO",
    description:
      "Danil — співзасновник та CTO Brandly. Інженер, який перетворює складні технічні ідеї на прості та надійні рішення для юристів. Відповідає за технічну архітектуру Brandly, стабільність платформи та швидке впровадження нових можливостей, що допомагають юристам працювати без помилок і затримок.",
    email: "danil@makarov.app",
    photoUrl: "/founder1.jpg",
    linkedIn: "https://www.linkedin.com/in/danil-makarov/",
    telegram: "https://t.me/dnmkr",
  },
  {
    name: "Bohdan Kuzmovych",
    moreAboutName: "Богдана",
    position: "Co-founder & CEO",
    description:
      "Bohdan — співзасновник та CEO Brandly, власник юридичної компанії Red Lawyers. Досвідчений юрист із глибоким розумінням сфери інтелектуальної власності. Поєднує правову експертизу з підприємницьким баченням, щоб зробити захист торгових марок доступним для кожного бізнесу.",
    email: "kuzmovychbohdan@gmail.com",
    photoUrl: "/founder2.jpg",
    linkedIn: "https://www.linkedin.com/in/lawyerbohdankuzmovych/",
  },
  {
    name: "Nazar Kostenko",
    moreAboutName: "Назара",
    position: "Co-founder, CLO",
    description:
      "Nazar — співзасновник та CLO Brandly. Забезпечує відповідність платформи законодавству, а також будує систему взаємодії з користувачами.",
    email: "",
    photoUrl: "/founder2.5.jpg",
  },
];

export const AboutUs = () => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const section = searchParams.get("section");

    if (section) {
      // Small delay to ensure DOM is ready after render
      const timeoutId = setTimeout(() => {
        const element = document.getElementById(section);

        element?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [searchParams]);

  return (
    <LandingLayout>
      <LandingSection>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 4, sm: 5, md: 6, lg: 8 },
          }}
        >
          {/* Header */}
          <FadeIn>
            <Box
              sx={{
                textAlign: "center",
                px: { xs: 2, sm: 3, md: 4, lg: 8 },
              }}
            >
              <Typography
                level="h1"
                sx={{
                  fontSize: {
                    xs: "2rem",
                    sm: "2.5rem",
                    md: "3rem",
                    lg: "4.25rem",
                  },
                  fontWeight: 600,
                  lineHeight: 1.15,
                  letterSpacing: "-0.02em",
                  mb: { xs: 1.5, md: 2 },
                }}
              >
                Команда{" "}
                <Box component="span" sx={{ color: "primary.500" }}>
                  Brandly
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: { xs: "14px", sm: "15px", md: "16px" },
                  lineHeight: "175%",
                  letterSpacing: "0.15px",
                  color: "text.secondary",
                  maxWidth: 600,
                  mx: "auto",
                }}
              >
                Ми — команда ентузіастів, які прагнуть спростити управління
                торговими марками для українського бізнесу.
              </Typography>
            </Box>
          </FadeIn>

          {/* Team Cards */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(3, 1fr)",
              },
              gap: { xs: 3, sm: 4, md: 5 },
              maxWidth: 1100,
              mx: "auto",
              width: "100%",
            }}
          >
            {TEAM_MEMBERS.map((member, index) => (
              <FadeIn key={member.name} delay={150 + index * 100}>
                <TeamMemberCard member={member} />
              </FadeIn>
            ))}
          </Box>

          {/* Contact Section */}
          <FadeIn delay={350}>
            <Card
              id={ABOUT_US_SECTIONS.CONTACT}
              variant="outlined"
              sx={{
                p: { xs: 3, sm: 4, md: 5 },
                maxWidth: 900,
                mx: "auto",
                width: "100%",
                textAlign: "center",
                bgcolor: "background.surface",
              }}
            >
              <Typography
                level="h2"
                sx={{
                  fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                  fontWeight: 600,
                  mb: { xs: 1, md: 1.5 },
                }}
              >
                Зв'яжіться з нами
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "14px", sm: "15px", md: "16px" },
                  mb: { xs: 3, md: 4 },
                }}
              >
                Маєте питання або хочете дізнатися більше про Brandly? Ми завжди
                раді допомогти.
              </Typography>

              {/* Emails */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 2, sm: 3 },
                  justifyContent: "center",
                  alignItems: "center",
                  mb: { xs: 3, md: 4 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Telegram sx={{ color: "primary.500", fontSize: 20 }} />
                  <Link
                    href="https://t.me/dnmkr"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontSize: { xs: "14px", sm: "15px" },
                      color: "text.primary",
                      textDecoration: "none",
                      "&:hover": {
                        color: "primary.500",
                      },
                    }}
                  >
                    @dnmkr
                  </Link>
                </Box>
                {TEAM_MEMBERS.filter((member) => member.email).map((member) => (
                  <Box
                    key={member.email}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Email sx={{ color: "primary.500", fontSize: 20 }} />
                    <Link
                      href={`mailto:${member.email}`}
                      sx={{
                        fontSize: { xs: "14px", sm: "15px" },
                        color: "text.primary",
                        textDecoration: "none",
                        "&:hover": {
                          color: "primary.500",
                        },
                      }}
                    >
                      {member.email}
                    </Link>
                  </Box>
                ))}
              </Box>
            </Card>
          </FadeIn>
        </Box>
      </LandingSection>
    </LandingLayout>
  );
};
